'use client'

import { usePathname } from 'next/navigation'
import {
  BarChart3, BookUser, DiamondPlus, LayoutDashboard, LibraryBig,
  RadioTower, ShieldCheck, Ticket,
} from 'lucide-react'
import { TooltipProvider } from '@elmnts/ui/tooltip'
import { ADMIN_HUB_ACCESS } from '@/app/(private)/admin/_lib/hubAccess'
import { CONTACTS_PERMISSIONS } from '@/lib/permissions/contacts'
import { DISPATCHES_RAIL_ACCESS } from '@/lib/permissions/dispatches'
import { channelIdFromPathname } from '@/lib/chatbotRoute'
import { dispatchesPath } from '@/lib/dispatches'
import { newConversationPath } from '@/lib/newConversation'
import { templatesPath } from '@/lib/templates'
import { PresenceRailButton } from '@/components/Presence/PresenceRailButton'
import { NotificationBell } from '@/components/notifications/NotificationBell'
import { BrandMenu } from './BrandMenu'
import { RailItem } from './RailItem'
import { UserRailButton } from './UserRailButton'

/** Itens canal-scoped: só habilitados dentro de um canal selecionado (`base`). */
export const CHANNEL_ITEMS = [
  { icon: LayoutDashboard, label: 'Kanban', path: 'kanban_view', require: 'protocols:read' },
] as const

/**
 * Contatos são da EMPRESA, não do canal (Task 24b) — item sempre habilitado, fora de
 * `CHANNEL_ITEMS`, com href fixo pra rota company-wide `/chatbot/contacts`.
 */
export const CONTACTS_ITEM = {
  icon: BookUser, label: 'Contatos', href: '/chatbot/contacts', require: CONTACTS_PERMISSIONS.READ,
} as const

/**
 * Transmissão segue a mesma lógica de Contatos: a lista é do tenant, então o item nunca
 * é desabilitado. Dentro de um canal aponta pra rota recortada (`{base}/transmission`),
 * fora aponta pra global — a MESMA view, só sem filtro.
 */
export const TRANSMISSION_ITEM = {
  icon: RadioTower, label: 'Transmissão', href: '/chatbot/transmission-groups',
  channelPath: 'transmission', require: 'transmission_groups:read',
} as const

/**
 * Vagas seguem o molde de Contatos (href fixo global, sempre habilitado), NÃO o de
 * Transmissão: o back ainda não pina `channel_id` no pool, então não existe recorte
 * por canal — um `channelPath` aqui apontaria pra uma rota que mente sobre o próprio
 * recorte (pedido ao back em docs/pedido-back-channel-scope-pools.md).
 */
export const POOLS_ITEM = {
  icon: Ticket, label: 'Vagas', href: '/chatbot/pools', require: 'pools:read',
} as const

/**
 * Disparos saiu de `CHANNEL_ITEMS`: a lista é do TENANT (a rota `/whatsapp/dispatches`
 * nunca recortou por canal), e a rota antiga sob `[channelId]` prometia um recorte que a
 * tela NÃO fazia. Segue o molde de Transmissão — href global, e dentro de um canal aponta
 * pro recorte — mas o recorte aqui é **query param**, não outra rota: o canal é um filtro
 * da tela, que o atendente muda sem sair do lugar. Item nunca é desabilitado.
 *
 * Gate = `dispatches:read` OU qualquer seção do Resumo (faixa 38, D8): a aba do Resumo é o
 * acesso, e quem tem só uma delas precisa chegar à tela.
 */
export const DISPATCHES_ITEM = {
  icon: BarChart3, label: 'Disparos', href: '/chatbot/dispatches', require: DISPATCHES_RAIL_ACCESS,
} as const

/**
 * Nova conversa saiu de `CHANNEL_ITEMS`: o wizard virou rota do TENANT, e
 * `protocols:messages:send` é permission do tenant — `checkPermission` casa contra a lista
 * plana do usuário (`src/lib/permissions/check.ts`), então o gate vale igual fora do canal.
 * Molde do `DISPATCHES_ITEM`: href global, e dentro de um canal o canal vira **query param**
 * (`?channel_id=`), não segmento. Item nunca é desabilitado.
 */
export const NEW_CONVERSATION_ITEM = {
  icon: DiamondPlus, label: 'Nova conversa', href: '/chatbot/new-conversation',
  require: 'protocols:messages:send',
} as const

/**
 * Templates saiu de `CHANNEL_ITEMS`: o catálogo é do TENANT
 * (`GET /whatsapp/message_templates` é escopado ao USER, nunca a um canal) e
 * `message_templates:read` é permission do tenant. A rota antiga sob `[channelId]` lia o
 * snapshot do canal — pré-filtrado por `find_actives` —, então nenhuma tela mostrava
 * template reprovado, inativo ou arquivado. Molde do `DISPATCHES_ITEM`: href global, e
 * dentro de um canal o canal vira **query param** (`?channel_id=`), não segmento. Item
 * nunca é desabilitado.
 */
export const TEMPLATES_ITEM = {
  icon: LibraryBig, label: 'Templates', href: '/chatbot/templates',
  require: 'message_templates:read',
} as const

export function AppRail() {
  const pathname = usePathname()
  const channelId = channelIdFromPathname(pathname)
  const base = channelId ? `/chatbot/${channelId}` : null

  return (
    <TooltipProvider delayDuration={200}>
      <nav className="flex h-full w-12 shrink-0 flex-col items-center gap-1 border-r border-sidebar-border bg-sidebar pb-2">
        {/* Marca = Home + troca de canal num só ícone (clique → home; hover → canais).
            h-14 (3.5rem) + border-b alinham a linha com o header do ChannelSideBar e o ChatNavBar. */}
        <div className="mb-1 flex h-14 w-full shrink-0 items-center justify-center border-b border-sidebar-border">
          <BrandMenu currentChannelId={channelId} active={pathname === '/chatbot'} />
        </div>
        <RailItem
          icon={CONTACTS_ITEM.icon}
          label={CONTACTS_ITEM.label}
          require={CONTACTS_ITEM.require}
          href={CONTACTS_ITEM.href}
          active={pathname.includes('/contacts')}
        />
        <RailItem
          icon={TRANSMISSION_ITEM.icon}
          label={TRANSMISSION_ITEM.label}
          require={TRANSMISSION_ITEM.require}
          href={base ? `${base}/${TRANSMISSION_ITEM.channelPath}` : TRANSMISSION_ITEM.href}
          active={
            pathname.startsWith(TRANSMISSION_ITEM.href) ||
            pathname.includes(`/${TRANSMISSION_ITEM.channelPath}`)
          }
        />
        <RailItem
          icon={POOLS_ITEM.icon}
          label={POOLS_ITEM.label}
          require={POOLS_ITEM.require}
          href={POOLS_ITEM.href}
          active={pathname.startsWith(POOLS_ITEM.href)}
        />
        <RailItem
          icon={DISPATCHES_ITEM.icon}
          label={DISPATCHES_ITEM.label}
          require={DISPATCHES_ITEM.require}
          href={dispatchesPath(channelId ?? undefined)}
          active={pathname.startsWith(DISPATCHES_ITEM.href)}
        />
        <RailItem
          icon={NEW_CONVERSATION_ITEM.icon}
          label={NEW_CONVERSATION_ITEM.label}
          require={NEW_CONVERSATION_ITEM.require}
          href={newConversationPath(channelId ?? undefined)}
          active={pathname.startsWith(NEW_CONVERSATION_ITEM.href)}
        />
        <RailItem
          icon={TEMPLATES_ITEM.icon}
          label={TEMPLATES_ITEM.label}
          require={TEMPLATES_ITEM.require}
          href={templatesPath(channelId ?? undefined)}
          active={pathname.startsWith(TEMPLATES_ITEM.href)}
        />
        {/* Item "Chat" removido — dentro do canal o sidebar de chats está sempre aberto;
            fora de canal o item nem funcionava (2026-07-02) */}
        {CHANNEL_ITEMS.map((item) => (
          <RailItem
            key={item.path}
            icon={item.icon}
            label={item.label}
            require={item.require}
            href={base ? `${base}/${item.path}` : undefined}
            disabled={!base}
            disabledHint="Selecione um canal"
            active={!!base && pathname.startsWith(`${base}/${item.path}`)}
          />
        ))}
        <div className="mt-auto flex flex-col items-center gap-1">
          <div className="my-1 h-px w-8 bg-sidebar-border" />
          {/* `ADMIN_HUB_ACCESS`, não `ADMIN_ACCESS`: o rail só aponta pro /admin quando o hub tem card. O layout segue
              aberto com `ADMIN_ACCESS` (link direto do "Vincular listas"). */}
          <RailItem icon={ShieldCheck} label="Admin" require={ADMIN_HUB_ACCESS} href="/admin" active={pathname.startsWith('/admin')} />
          {/* Sino sem `require`: o inbox é auth-only no back (visibilidade é
              por targeting per-user, não por grant) — gatear por permissão
              esconderia notificação de quem tem direito a ela. */}
          <NotificationBell />
          <PresenceRailButton />
          <UserRailButton />
        </div>
      </nav>
    </TooltipProvider>
  )
}
